import { useMemo } from 'react';
import once from 'lodash/once';
import { v4 as uuid } from 'uuid';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { AnalyticsAttributesUntyped } from '@atlassian/jira-product-analytics-bridge/src/types';
import {
	fireTrackAnalytics,
	type ActionSubjectAndAction,
} from '@atlassian/jira-product-analytics-bridge';

// Defer instantiation of the ID until it's requested, then use the same ID forever after that
const getSingleInstrumentationID = once(() => uuid());

export const AI_EVENTS: Record<string, ActionSubjectAndAction> = {
	INIT: 'aiInteraction initiated',
	VIEWED: 'aiResult viewed',
	ACTIONED: 'aiResult actioned',
	DISMISSED: 'aiInteraction dismissed',
	FEEDBACK: 'aiFeedback submitted',
	ERROR: 'aiResult error',
} as const;

type AI_EVENTS_TYPE = (typeof AI_EVENTS)[keyof typeof AI_EVENTS];

export const useSmartRelatedIssuesAIAnalytics = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const actions = useMemo(() => {
		const event = createAnalyticsEvent({});
		return {
			fireTrackAI: (
				eventName: AI_EVENTS_TYPE,
				attr: Omit<AnalyticsAttributesUntyped, 'singleInstrumentationID'> = {},
			) => {
				fireTrackAnalytics(event, eventName, {
					product: 'jira',
					subproduct: 'serviceDesk',
					aiFeatureName: 'smartRelatedIssuesPanel',
					proactiveGeneratedAI: 0,
					userGeneratedAI: 1,
					isAIFeature: 0,
					singleInstrumentationID: getSingleInstrumentationID(),
					...attr,
				});
			},
		};
	}, [createAnalyticsEvent]);

	return actions;
};
